"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2, CheckCircle2 } from "lucide-react"

interface ResendVerificationProps {
  /** Prefill the address (e.g. the email just registered with or signed in). */
  email?: string
  /** Show an editable email field. When false, `email` must be provided. */
  showInput?: boolean
  /** Visual size of the trigger button. */
  size?: "sm" | "default"
  className?: string
}

/**
 * Re-request a verification email. The endpoint always responds 200 (to avoid
 * leaking which addresses are registered), so a resolved request is treated as
 * "sent" regardless of whether an unverified account actually matched.
 */
export function ResendVerification({
  email: initialEmail = "",
  showInput = true,
  size = "default",
  className,
}: ResendVerificationProps) {
  const [email, setEmail] = useState(initialEmail)
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle")

  const handleResend = async () => {
    if (!email) return
    setStatus("sending")
    try {
      await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })
    } catch {
      // Swallow — the endpoint is anti-enumeration and we never surface details.
    } finally {
      setStatus("sent")
    }
  }

  if (status === "sent") {
    return (
      <p className={`flex items-center gap-2 text-sm text-green-600 ${className ?? ""}`}>
        <CheckCircle2 className="h-4 w-4" />
        <span>If an unverified account exists, a new verification link has been sent.</span>
      </p>
    )
  }

  return (
    <div className={`space-y-2 ${className ?? ""}`}>
      {showInput && (
        <div className="space-y-1">
          <Label htmlFor="resend-email">Email address</Label>
          <Input
            id="resend-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            disabled={status === "sending"}
          />
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size={size}
        onClick={handleResend}
        disabled={status === "sending" || !email}
        className="w-full"
      >
        {status === "sending" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        <span>Resend verification email</span>
      </Button>
    </div>
  )
}
